<!-- =========================================================================================
    File Name: CellRendererActions.vue
    Description: Edit & Delete Organization Modal (UI Strings -> API Integers)
========================================================================================== -->

<template>
  <div class="flex items-center justify-center w-full h-full" :style="{ direction: $vs.rtl ? 'rtl' : 'ltr' }">
    <!-- Action Icons -->
    <feather-icon
      icon="Edit3Icon"
      svgClasses="h-5 w-5 mr-5 text-primary hover:opacity-75 cursor-pointer transition-colors"
      @click="editRecord"
    />
    <feather-icon
      icon="Trash2Icon"
      svgClasses="h-5 w-5 text-danger hover:opacity-75 cursor-pointer transition-colors"
      @click="confirmDeleteRecord"
    />

    <!-- Modern Update Prompt -->
    <vs-prompt
      :title="$t('updatecompany.title')"
      :accept-text="$t('Update')"
      :cancel-text="$t('Cancel')"
      @cancel="clearFields"
      @accept="update"
      @close="clearFields"
      :is-valid="isFormValid"
      :active.sync="activePrompt"
      class="modern-org-modal"
    >
      <form @submit.prevent class="modern-form-wrapper">
        
        <!-- SECTION 1: Identity & Credentials -->
        <div class="form-section">
          <h6 class="section-title">Organization Identity</h6>
          
          <div class="vx-row">
            <div class="vx-col w-full md:w-1/2 mb-4">
              <label class="field-label">{{ $t('addcompany.lbl0') }}</label>
              <vs-input
                name="company_name"
                v-model="company_name"
                class="w-full readonly-input"
                icon-pack="feather"
                icon="icon-briefcase"
                icon-no-border
                disabled
              />
            </div>

            <div class="vx-col w-full md:w-1/2 mb-4">
              <label class="field-label">{{ $t('addcompany.lbl1') }}</label>
              <vs-input
                name="company_email"
                v-model="company_email"
                class="w-full readonly-input"
                icon-pack="feather"
                icon="icon-mail"
                icon-no-border
                disabled
              />
            </div>
          </div>

          <div class="vx-row">
            <div class="vx-col w-full md:w-1/2 mb-4">
              <label class="field-label">{{ $t('updatecompany.lbl2') }} <span class="text-xs text-muted font-normal">(Leave blank to keep current)</span></label>
              <vs-input
                name="company_password"
                v-model="company_password"
                class="w-full"
                type="password"
                placeholder="New Password"
                icon-pack="feather"
                icon="icon-lock"
                icon-no-border
              />
            </div>

            <div class="vx-col w-full md:w-1/2 mb-4">
              <label class="field-label">{{ $t('updatecompany.lbl3') }}</label>
              <vs-input
                name="confirm_password"
                v-model="confirm_password"
                class="w-full"
                :danger="passwordsDoNotMatch"
                :success="passwordsMatch"
                type="password"
                placeholder="Confirm New Password"
                icon-pack="feather"
                icon="icon-check"
                icon-no-border
              />
              <span class="text-danger text-xs mt-1 block" v-if="passwordsDoNotMatch">
                Passwords do not match
              </span>
            </div>
          </div>
        </div>

        <!-- SECTION 2: Survey Intervals -->
        <div class="form-section">
          <h6 class="section-title">Survey Cadence & Intervals</h6>
          
          <div class="vx-row">
            <div class="vx-col w-full sm:w-1/3 mb-4">
              <label class="field-label">{{ $t('addcompany.lbl3') }}</label>
              <div class="input-with-badge">
                <vs-input
                  v-validate="'numeric|between:1,99'"
                  name="coaching_survey"
                  v-model="coaching_survey"
                  class="w-full"
                  placeholder="30"
                  type="number"
                />
                <span class="badge-unit">Days</span>
              </div>
            </div>

            <div class="vx-col w-full sm:w-1/3 mb-4">
              <label class="field-label">{{ $t('addcompany.lbl4') }}</label>
              <div class="input-with-badge">
                <vs-input
                  v-validate="'numeric|between:1,99'"
                  name="motivation_survey"
                  v-model="motivation_survey"
                  class="w-full"
                  placeholder="15"
                  type="number"
                />
                <span class="badge-unit">Days</span>
              </div>
            </div>

            <div class="vx-col w-full sm:w-1/3 mb-4">
              <label class="field-label">{{ $t('addcompany.lbl8') }}</label>
              <div class="input-with-badge">
                <vs-input
                  v-validate="'numeric|between:1,99'"
                  name="feedback_frequency"
                  v-model="feedback_frequency"
                  class="w-full"
                  placeholder="15"
                  type="number"
                />
                <span class="badge-unit">Days</span>
              </div>
            </div>
          </div>
        </div>

        <!-- SECTION 3: Platform Settings & Branding -->
        <div class="form-section">
          <h6 class="section-title">Platform Configuration</h6>
          
          <div class="vx-row">
            <!-- Org Type -->
            <div class="vx-col w-full md:w-1/3 mb-4">
              <label class="field-label">Org Type</label>
              <v-select
                class="custom-v-select w-full"
                v-model="organization_type"
                :options="organization_type_list"
                :reduce="item => item.value"
                label="text"
                placeholder="Select Org Type"
              />
            </div>

            <!-- High-Contrast Segmented Terminology Toggle (STRINGS) -->
            <div class="vx-col w-full md:w-1/3 mb-4">
              <label class="field-label">{{ $t('addcompany.lbl5') }}</label>
              <div class="segmented-control">
                <button
                  type="button"
                  :class="['segment-btn', { 'active-btn': terminology_type === '0' }]"
                  @click="terminology_type = '0'"
                >
                  COACH
                </button>
                <button
                  type="button"
                  :class="['segment-btn', { 'active-btn': terminology_type === '1' }]"
                  @click="terminology_type = '1'"
                >
                  DPM
                </button>
                <button
                  type="button"
                  :class="['segment-btn', { 'active-btn': terminology_type === '2' }]"
                  @click="terminology_type = '2'"
                >
                  SUPER
                </button>
              </div>
            </div>

            <!-- Enterprise Switch -->
            <div class="vx-col w-full md:w-1/3 mb-4 flex items-center">
              <div class="enterprise-toggle-card">
                <div>
                  <span class="font-medium text-dark text-xs block">{{ $t('addcompany.lbl6') }}</span>
                  <span class="text-muted text-xs">Enterprise tier</span>
                </div>
                <vs-switch color="success" v-model="enterprise_type" name="enterprise_type">
                  <span slot="on">Paid</span>
                  <span slot="off">Free</span>
                </vs-switch>
              </div>
            </div>
          </div>

          <!-- Color Customization -->
          <div class="vx-row mt-2">
            <div class="vx-col w-full md:w-1/2 mb-4">
              <label class="field-label mb-2 block">{{ $t('addcompany.lbl7') }}</label>
              <div class="theme-palette">
                <button
                  type="button"
                  v-for="color in themeColors"
                  :key="color"
                  class="color-swatch"
                  :style="{ backgroundColor: color }"
                  :class="{ active: theme_color.toUpperCase() === color.toUpperCase() }"
                  @click="setThemeColor(color)"
                >
                  <i v-if="theme_color.toUpperCase() === color.toUpperCase()" class="feather icon-check"></i>
                </button>
              </div>
            </div>

            <!-- Secondary Color with Live Color Picker -->
            <div class="vx-col w-full md:w-1/2 mb-4">
              <label class="field-label">{{ $t('addcompany.lbl9') }}</label>
              <div class="flex items-center">
                <vs-input
                  name="secondary_color"
                  v-model="secondary_color"
                  class="w-full"
                  placeholder="#000000"
                  @blur="ensureHashColor"
                />
                <!-- Interactive Color Picker Box -->
                <label 
                  class="color-preview-box cursor-pointer" 
                  :style="{ backgroundColor: safeSecondaryColor }"
                  title="Click to pick color"
                >
                  <input 
                    type="color" 
                    v-model="safeSecondaryColor" 
                    @input="onNativeColorPick"
                    class="hidden-native-picker"
                  />
                </label>
              </div>
            </div>
          </div>
        </div>

      </form>
    </vs-prompt>
  </div>
</template>

<script>
import VuePerfectScrollbar from 'vue-perfect-scrollbar';
import { models } from 'feathers-vuex';
import MD5 from 'crypto-js/md5';
import i18n from '@/i18n/i18n';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';

export default {
  name: "CellRendererActions",
  components: {
    VuePerfectScrollbar,
    vSelect
  },
  data () {
    return {
      activePrompt: false,
      company_email: "",
      company_name: "",
      company_password: "",
      confirm_password: "",
      terminology_type: "0", // Now matches AddCompany as a string
      enterprise_type: false,
      organization_type: "Business",
      organization_type_list: [
        { value: 'Business', text: 'Business' },
        { value: 'Academic', text: 'Academic' },
        { value: 'Recruiter', text: 'Recruiter' }
      ],
      coaching_survey: '30',
      motivation_survey: '30',
      feedback_frequency: '15',
      theme_color: '#9DCD5B',
      themeColors: [
        '#9DCD5B',
        '#3BA0BE',
        '#6859B4',
        '#E4D033',
        '#EA7526',
        '#C6211B',
        '#F36677',
        '#4A5568'
      ],
      secondary_color: '#000000'
    };
  },
  computed: {
    safeSecondaryColor: {
      get () {
        if (!this.secondary_color) return '#000000';
        return String(this.secondary_color).startsWith('#') 
          ? this.secondary_color 
          : '#' + this.secondary_color;
      },
      set (val) {
        this.secondary_color = val;
      }
    },
    passwordsDoNotMatch () {
      return this.company_password !== '' && this.company_password !== this.confirm_password;
    },
    passwordsMatch () {
      return this.company_password !== '' && this.company_password === this.confirm_password;
    },
    isFormValid () {
      if (this.company_password !== '' && this.company_password !== this.confirm_password) {
        return false;
      }
      return !this.errors.any() && this.company_email !== '';
    }
  },
  methods: {
    ensureHashColor () {
      if (this.secondary_color && !String(this.secondary_color).startsWith('#')) {
        this.secondary_color = '#' + String(this.secondary_color).trim();
      }
    },
    onNativeColorPick (e) {
      this.secondary_color = e.target.value;
    },
    clearFields () {
      this.company_password = "";
      this.confirm_password = "";
    },
    setThemeColor (color) {
      this.theme_color = color;
    },
    editRecord () {
      this.activePrompt = true;
      const id = this.params.data.id;

      this.$store
        .dispatch("companyManagement/fetchCompany", id)
        .then(res => {
          this.company_email = res.email || "";
          this.company_name = res.name || "";
          
          // Terminology: Read whatever DB gives us, convert it to a string for Vue UI
          let term = res.terminology;
          if (term === undefined || term === null) term = res.terminology_type;
          this.terminology_type = (term !== undefined && term !== null) ? String(term) : "0";

          // Enterprise
          let ent = res.enterpriseType;
          if (ent === undefined || ent === null) ent = res.enterprise_type;
          this.enterprise_type = Boolean(ent);
          
          // Coaching
          this.coaching_survey = String(res.coachingSurveyDays || res.coaching_survey_days || 30);
          
          // Motivation (Fix for Singular vs Plural DB columns)
          let mot = res.motivationSurveyDay;
          if (mot === undefined || mot === null) mot = res.motivationSurveyDays;
          if (mot === undefined || mot === null) mot = res.motivation_survey_day;
          this.motivation_survey = mot ? String(mot) : "30";
          
          this.feedback_frequency = String(res.feedbackFrequency || res.feedback_frequency || 15);
          
          // Org type
          const orgVal = res.organizationType || res.organization_type || "Business";
          this.organization_type = typeof orgVal === 'object' && orgVal !== null ? orgVal.value : orgVal;

          // Colors
          this.theme_color = res.themeColor || res.theme_color || "#9DCD5B";
          
          const secColor = res.secondaryColor || res.secondary_color || "#000000";
          this.secondary_color = String(secColor).startsWith('#') ? secColor : '#' + secColor;
        })
        .catch(err => {
          console.error("Failed to fetch company", err);
        });
    },
    update () {
      this.ensureHashColor();

      const resolvedOrgType = typeof this.organization_type === 'object' && this.organization_type !== null
        ? this.organization_type.value 
        : this.organization_type;

      // Convert our UI Strings BACK to absolute Integers before sending to API!
      const numTerminology = parseInt(this.terminology_type, 10);
      const numMot = parseInt(this.motivation_survey, 10);
      const numCoach = parseInt(this.coaching_survey, 10);
      const numFeed = parseInt(this.feedback_frequency, 10);

      const payload = {
        id: this.params.data.id,
        email: this.company_email,
        name: this.company_name,
        enterpriseType: this.enterprise_type ? 1 : 0,
        enterprise_type: this.enterprise_type ? 1 : 0,
        
        terminology: numTerminology,
        terminology_type: numTerminology,
        
        coachingSurveyDays: numCoach,
        coaching_survey_days: numCoach,
        
        motivationSurveyDay: numMot,
        motivationSurveyDays: numMot,
        motivation_survey_day: numMot,
        
        feedbackFrequency: numFeed,
        feedback_frequency: numFeed,
        
        organizationType: resolvedOrgType,
        organization_type: resolvedOrgType,
        themeColor: this.theme_color,
        theme_color: this.theme_color,
        secondaryColor: this.safeSecondaryColor,
        secondary_color: this.safeSecondaryColor
      };

      if (this.company_password && this.company_password.trim() !== '') {
        payload.companyPassword = MD5(this.company_password).toString();
      }

      const company = new models.api.Company(payload);
      Object.assign(company, payload); // Force all payload fields in

      this.$store
        .dispatch("companyManagement/updateCompany", company)
        .then(res => {
          this.$vs.notify({
            title: i18n.t('notification.admin.company.updateCompany.success.u1'),
            text: i18n.t('notification.admin.company.updateCompany.success.u2'),
            color: "success"
          });
          this.clearFields();
          
          if (this.params && this.params.context && this.params.context.componentParent) {
            this.params.context.componentParent.fetchCompanies();
          } else {
            setTimeout(() => window.location.reload(), 600);
          }
        })
        .catch(err => {
          console.error("Company update failed:", err);
          this.$vs.notify({
            title: i18n.t('notification.admin.company.updateCompany.error.u1'),
            text: i18n.t('notification.admin.company.updateCompany.error.u2'),
            color: "danger"
          });
        });
    },
    confirmDeleteRecord () {
      this.$store
        .dispatch("users/find", { query: { companyId: this.params.data.id } })
        .then(res => {
          if (res.total > 0) {
            this.$vs.dialog({
              type: "confirm",
              title: i18n.t('notification.admin.company.confirmDelete.confirm.c1'),
              text: i18n.t('notification.admin.company.confirmDelete.confirm.c2'),
              acceptText: "Ok"
            });
          } else {
            this.$vs.dialog({
              type: "confirm",
              color: "danger",
              title: i18n.t('notification.admin.company.confirmDelete.delete.d1'),
              text: i18n.t('notification.admin.company.confirmDelete.delete.d2', { name: this.params.data.name }),
              accept: this.deleteRecord,
              acceptText: "Delete"
            });
          }
        })
        .catch(err => console.error("Error checking dependencies", err));
    },
    deleteRecord () {
      this.$store
        .dispatch("companyManagement/removeCompany", this.params.data.id)
        .then(res => {
          this.$vs.notify({
            color: "success",
            title: i18n.t('notification.admin.company.removeCompany.success.r1'),
            text: i18n.t('notification.admin.company.removeCompany.success.r2'),
          });
          setTimeout(() => window.location.reload(), 600);
        })
        .catch(err => {
          this.$vs.notify({
            color: "danger",
            title: i18n.t('notification.admin.company.removeCompany.error.r1'),
            text: i18n.t('notification.admin.company.removeCompany.error.r2'),
          });
        });
    }
  }
};
</script>

<style scoped>
.modern-form-wrapper {
  padding: 8px 4px;
}

.readonly-input >>> input {
  background-color: #f8fafc !important;
  cursor: not-allowed;
  color: #64748b;
}

.form-section {
  border-bottom: 1px solid #edf2f7;
  padding-bottom: 14px;
  margin-bottom: 16px;
}
.form-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}
.section-title {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #718096;
  margin-bottom: 12px;
}

.field-label {
  display: block;
  font-size: 0.8rem;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 5px;
}

.input-with-badge {
  position: relative;
  display: flex;
  align-items: center;
}
.badge-unit {
  position: absolute;
  right: 12px;
  top: 9px;
  font-size: 0.75rem;
  color: #a0aec0;
  pointer-events: none;
}

/* High Contrast Segmented Control */
.segmented-control {
  display: flex;
  background-color: #e2e8f0;
  padding: 3px;
  border-radius: 8px;
  border: 1px solid #cbd5e1;
}
.segment-btn {
  flex: 1;
  padding: 7px 8px;
  border: none;
  background: transparent;
  font-size: 0.75rem;
  font-weight: 700;
  color: #475569;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
}
.segment-btn.active-btn {
  background-color: rgb(var(--vs-primary)) !important;
  color: #ffffff !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
}

/* Enterprise Toggle Card */
.enterprise-toggle-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #f8fafc;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  width: 100%;
}

/* Color Swatches */
.theme-palette {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.color-swatch {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid #ffffff;
  box-shadow: 0 0 0 1px #cbd5e1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  outline: none;
}
.color-swatch:hover {
  transform: scale(1.1);
}
.color-swatch.active {
  box-shadow: 0 0 0 2px rgb(var(--vs-primary));
  transform: scale(1.15);
}
.color-swatch i {
  color: #ffffff;
  font-size: 14px;
}

/* Color Preview & Native Picker */
.color-preview-box {
  position: relative;
  width: 38px;
  height: 38px;
  border-radius: 8px;
  border: 2px solid #cbd5e1;
  margin-left: 8px;
  flex-shrink: 0;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  overflow: hidden;
}
.hidden-native-picker {
  opacity: 0;
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  cursor: pointer;
}
</style>

<style>
.modern-org-modal .vs-dialog {
  max-width: 720px !important;
  border-radius: 12px !important;
  overflow: hidden;
}
.modern-org-modal .vs-dialog-text {
  max-height: 75vh;
  overflow-y: auto;
  padding: 1.25rem 1.75rem !important;
}
.modern-org-modal .vs-dialog-header {
  border-bottom: 1px solid #f1f5f9;
  padding: 1.25rem 1.75rem !important;
}
.modern-org-modal .vs-dialog-footer {
  border-top: 1px solid #f1f5f9;
  padding: 1rem 1.75rem !important;
}
</style>